import React from 'react';
import { observer } from 'mobx-react-lite';
import Badge from '../Badge/Badge';
import SelectDropdown from '../Dropdown/SelectDropdown';
import { XCircleIcon, PaintBrushIcon } from '@heroicons/react/20/solid'; // For remove button

// --- Define Preset Styles ---
// We'll use the background/text color pairs compatible with the Badge component
// You can customize this list extensively
const presetStyleOptions = [
    { value: 'remove', label: 'Remove Style', icon: <XCircleIcon className="h-4 w-4 text-gray-400" />, destructive: true },
    { type: 'divider' },
    { value: { backgroundColor: '#ecfdf5', textColor: '#047857' }, label: 'Green' }, // Success/Done
    { value: { backgroundColor: '#eff6ff', textColor: '#1d4ed8' }, label: 'Blue' }, // In Progress
    { value: { backgroundColor: '#fefce8', textColor: '#854d0e' }, label: 'Yellow' }, // Review/Warning
    { value: { backgroundColor: '#fef2f2', textColor: '#b91c1c' }, label: 'Red' }, // Blocked/Error
    { value: { backgroundColor: '#f8fafc', textColor: '#475569' }, label: 'Gray' }, // Default/Planning
    { value: { backgroundColor: '#faf5ff', textColor: '#7e22ce' }, label: 'Purple' }, // Enhancement/Feature
    { value: { backgroundColor: '#fffbeb', textColor: '#b45309' }, label: 'Amber' },
    { value: { backgroundColor: '#ecfeff', textColor: '#0e7490' }, label: 'Cyan' },
    { value: { backgroundColor: '#f0fdfa', textColor: '#0f766e' }, label: 'Teal' },
    { value: { backgroundColor: '#fdf4ff', textColor: '#a21caf' }, label: 'Fuchsia' },
    { value: { backgroundColor: '#fff1f2', textColor: '#be123c' }, label: 'Pink' },
    // Add more presets as needed
].map(option => {
    // If the option has a color value, add a Badge preview to the label
    if (option.value && typeof option.value === 'object') {
        return {
            ...option,
            label: (
                <div className="flex items-center gap-2">
                    <Badge text={option.label} styleOverride={option.value} />
                </div>
            ),
        };
    }
    return option; // Return dividers and remove option as is
});

// Helper function to find the preset label based on style colors
const findPresetLabel = (style) => {
    if (!style || !style.backgroundColor || !style.textColor) return "Default";

    // Normalize colors for comparison (remove '#' and ensure uppercase hex)
    const normalizeColor = (color) => color ? color.toUpperCase().replace('#', '') : null;
    const currentBg = normalizeColor(style.backgroundColor);
    const currentText = normalizeColor(style.textColor);

    const preset = presetStyleOptions.find(opt => {
        if (opt.value && typeof opt.value === 'object') {
            const presetBg = normalizeColor(opt.value.backgroundColor);
            const presetText = normalizeColor(opt.value.textColor);
            // Compare background and text colors (allow for potential alpha channel differences in ExcelJS ARGB)
            return presetBg && currentBg && presetBg.slice(-6) === currentBg.slice(-6) &&
                   presetText && currentText && presetText.slice(-6) === currentText.slice(-6);
        }
        return false;
    });

    // The label in presetStyleOptions might be a React element (the Badge preview)
    // We need the original simple string label. Let's find it based on the value.
    if (preset) {
         // Find the *original* text label from the raw options before mapping
         // This assumes the raw options are accessible or we redefine them slightly
         // Let's redefine the preset options structure slightly for easier access
         const rawPresets = [ // Redefine temporarily for lookup - ideally import this
            { value: 'remove', label: 'Remove Style' },
            { type: 'divider' },
            { value: { backgroundColor: '#ecfdf5', textColor: '#047857' }, label: 'Green' },
            { value: { backgroundColor: '#eff6ff', textColor: '#1d4ed8' }, label: 'Blue' },
            { value: { backgroundColor: '#fefce8', textColor: '#854d0e' }, label: 'Yellow' },
            { value: { backgroundColor: '#fef2f2', textColor: '#b91c1c' }, label: 'Red' },
            { value: { backgroundColor: '#f8fafc', textColor: '#475569' }, label: 'Gray' }, // Default/Planning
            { value: { backgroundColor: '#faf5ff', textColor: '#7e22ce' }, label: 'Purple' },
            { value: { backgroundColor: '#fffbeb', textColor: '#b45309' }, label: 'Amber' },
            { value: { backgroundColor: '#ecfeff', textColor: '#0e7490' }, label: 'Cyan' },
            { value: { backgroundColor: '#f0fdfa', textColor: '#0f766e' }, label: 'Teal' },
            { value: { backgroundColor: '#fdf4ff', textColor: '#a21caf' }, label: 'Fuchsia' },
            { value: { backgroundColor: '#fff1f2', textColor: '#be123c' }, label: 'Pink' },
         ];
         const rawPresetMatch = rawPresets.find(rp =>
             rp.value && typeof rp.value === 'object' &&
             normalizeColor(rp.value.backgroundColor)?.slice(-6) === currentBg?.slice(-6) &&
             normalizeColor(rp.value.textColor)?.slice(-6) === currentText?.slice(-6)
         );
         return rawPresetMatch ? rawPresetMatch.label : "Styled"; // Fallback label if match found but raw label missing
    }

    return "Default"; // If no preset matches, assume default or custom
};

// Define the explicit colors for the default 'Gray' style
const defaultStyleColors = { backgroundColor: '#f8fafc', textColor: '#475569' };

export const OptionStyleEditor = observer(({
    column,
    getColumnOptionStyles,
    setColumnOptionStyle,
    removeColumnOptionStyle,
    onClose, // Optional: if the editor needs to close itself
    // onUpdateValidation, // Add if you want to manage validation options here too
}) => {
    if (!column || !column.type.hasCustomValidation) {
        return <div className="p-4 text-sm text-red-600">Invalid column or column type for style editing.</div>;
    }

    // Get the current styles map for this column
    // Note: This assumes getColumnOptionStyles returns the observable map directly
    const currentStyles = getColumnOptionStyles(column.id) || new Map();

    const handleStyleChange = (optionValue, selectedStyleValue) => {
        if (selectedStyleValue === 'remove') {
            // Remove the style
            removeColumnOptionStyle(column.id, optionValue);
        } else if (selectedStyleValue && typeof selectedStyleValue === 'object') {
            // --- REVERT: Create minimal style object here ---
            const excelStyle = {
                fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: selectedStyleValue.backgroundColor.replace('#', 'FF') } },
                font: { color: { argb: selectedStyleValue.textColor.replace('#', 'FF') } }
                // No defaults needed here
            };
            // --- END REVERT ---
            console.log(`[OptionStyleEditor DBG] Created MINIMAL excelStyle for "${optionValue}":`, JSON.stringify(excelStyle));
            setColumnOptionStyle(column.id, optionValue, excelStyle);
        }
    };

    return (
        <div className="space-y-2 bg-white">
            {column.validationOptions.map((optionValue) => {
                const currentExcelStyle = currentStyles.get(optionValue);

                // Determine the background and text colors from the stored style, if any
                const derivedBgColor = currentExcelStyle?.fill?.fgColor?.argb
                    ? `#${currentExcelStyle.fill.fgColor.argb.slice(-6)}`
                    : null;
                const derivedTextColor = currentExcelStyle?.font?.color?.argb
                    ? `#${currentExcelStyle.font.color.argb.slice(-6)}`
                    : null;

                // If derived colors exist, use them. Otherwise, use the explicit default gray colors.
                const bgColorToPass = derivedBgColor || defaultStyleColors.backgroundColor;
                const textColorToPass = derivedTextColor || defaultStyleColors.textColor;

                return (
                    <div key={optionValue} className="p-2 border border-gray-200 rounded-md bg-white shadow-sm">
                        <div className="flex items-center justify-between gap-1">
                            <span className="flex-1 text-gray-700 truncate text-sm font-medium" title={optionValue}>
                                {optionValue || '(Empty Option)'}
                            </span>
                            <SelectDropdown
                                options={presetStyleOptions}
                                onChange={(selectedStyle) => {
                                    console.log('[OptionStyleEditor] SelectDropdown onChange invoked. Selected:', selectedStyle);
                                    handleStyleChange(optionValue, selectedStyle);
                                }}
                                value={undefined}
                                placeholder="Select Style"
                                showDropdownButton={false}
                                customTrigger={
                                    // --- Restore Original Trigger ---
                                    <div className="cursor-pointer flex items-center gap-1.5 hover:opacity-80 transition-opacity p-1 rounded hover:bg-gray-100">
                                        <Badge
                                            text={optionValue || '(Empty Option)'}
                                            // Pass the explicitly determined colors
                                            bgColor={bgColorToPass}
                                            textColor={textColorToPass}
                                        />
                                         <PaintBrushIcon className="h-4 w-4 text-gray-400" />
                                    </div>
                                    // --- End Restore Original Trigger ---
                                }
                                position="bottom-end"
                            />
                        </div>
                    </div>
                );
            })}

            {column.validationOptions.length === 0 && (
                 <div className="p-2 border border-gray-200 rounded-md bg-gray-50">
                    <p className="text-xs text-gray-500 text-center">No options defined for this column yet. Add options via the column header menu to assign styles.</p>
                 </div>
            )}

            {/* Optional: Add validation options editor here */}
            {/* <ColumnRowValidationEditor column={column} onUpdateValidation={onUpdateValidation} /> */}
        </div>
    );
}); 